// The engine's OWN evaluator, not a copy of it. The misset warning below is the
// one thing this screen owes the player before they commit chips, and a second
// implementation of "is the back at least as strong as the middle" would drift
// from the server's — on the day it did, the board would clear a foul the engine
// then charges three rows and a scoop for. These subpaths are the pure rules and
// ruleset modules: no state, no RNG, no node builtins, so they bundle unchanged.
import {
  bestArrangement,
  byStrength,
  categoryOf,
  detectNatural,
  middleGeFront,
  notFoul,
  rank3,
  rank5,
  royaltyUnits,
  type Category,
} from '@tira/engines/pusoy/rules';
import { NATURAL_META, RULESETS, STANDARD, type PusoyRuleset } from '@tira/engines/pusoy/ruleset';
import { useEffect, useMemo, useState, type CSSProperties, type DragEvent, type JSX } from 'react';
import { Icon } from '../../components/Icon.js';
import { useLang, useT, type CopyKey, type Lang, type T } from '../../i18n/index.js';
import { socket } from '../../net/socket.js';
import { useApp } from '../../state/store.js';
import { GameRoom } from '../parts/GameRoom.js';
import './PusoyBoard.css';

/**
 * S9 — Pusoy on the balkonahe (§G4). Presentation only: the server scores every
 * board, and this screen's job is to let a player build one and then watch the
 * rows come over.
 *
 * STAGING. There is no mock for this station, so it follows the rooftop's recipe
 * (design §4, "the same recipe as the other stations") in the venue's own lawa
 * accent. A Pusoy board is three hands pushed across a table, so that is exactly
 * what sits on the felt: five, five and three, back edge to near edge, laid flat.
 * The thirteen you have not placed yet are held rather than laid, so they wait on
 * the near rail in screen space. Only the row markers carry `up` — a row's name
 * and its rank read are labels floating over the felt, not objects on it.
 *
 * TWO THINGS THIS BOARD OWES THE PLAYER, and both are why it evaluates locally.
 *
 *  · THE MISSET WARNING. A board whose rows run out of order is not rejected —
 *    it is penalised, and brutally: the clean side takes all three rows plus the
 *    scoop bonus plus its royalties before a card is compared. So a player must
 *    be able to see they are about to foul BEFORE they lock in, which no amount
 *    of server round-tripping can do; the engine's own `notFoul` runs here.
 *  · THE AUTO-SORT ASSIST. `bestArrangement` is the same enumeration the engine
 *    plays for an absent seat, so the assist hands over exactly the board the
 *    clock would have — and then lets the player argue with it.
 *
 * ARRANGEMENT LOOKS SIMULTANEOUS AND IS NOT. The engine asks the seats in order
 * so that every wait has a real deadline (see the note in the engine's types.ts),
 * and nothing anybody submits is visible until the last seat locks in. This
 * screen leans into that: you may arrange whenever you like, and only the lock-in
 * button waits for your turn.
 */

/* ── The view, as the engine publishes it ─────────────────────────────────── */

export type PusoyPhase = 'arrange' | 'reveal';
export type RowName = 'back' | 'middle' | 'front';
export type Side = 'a' | 'b';

/** A board once it is face up. Empty until the last seat has locked in. */
export interface RevealedBoard {
  seat: number;
  back: string[];
  middle: string[];
  front: string[];
  classes: { back: Category; middle: Category; front: Category };
  misset: boolean;
  natural: string | null;
  royalties: number;
  /** Locked in by the clock rather than by its owner. Public at any table. */
  auto: boolean;
}

/**
 * One pair of seats, scored. The engine's own `PairResult` carries more — the
 * row-by-row verdict, the natural that fired, each side's royalties — and this
 * reads the two fields the screen renders: the net, and whether it was a scoop.
 */
export interface PairResultView {
  a: number;
  b: number;
  unitsA: number;
  unitsB: number;
  scoop: { winner: Side; bonus: number } | null;
}

export interface PusoyOverView {
  reason: string;
  winner: number | null;
  units: number[];
  order: number[];
  pairwise: PairResultView[];
}

export interface PusoyView {
  seats: number;
  /** Seat index, or null on the rail. */
  you: number | null;
  hand: string[];
  counts: number[];
  /** Who has locked in. Public — this is the table waiting on somebody. */
  locked: boolean[];
  auto: boolean[];
  turn: number | null;
  phase: PusoyPhase;
  boards: RevealedBoard[];
  quit: number[];
  over: PusoyOverView | null;
  ruleset: string;
}

/** The engine's own action shapes. */
type PusoyAction =
  | { kind: 'arrange'; back: string[]; middle: string[]; front: string[] }
  | { kind: 'auto' };

/** A miron arrives with this seat index — no seat, no taps, no "mine". */
const SPECTATOR_SEAT = -1;

interface Rows {
  back: string[];
  middle: string[];
  front: string[];
}

type Slot = RowName | 'pool';

const ROW_KEYS: RowName[] = ['back', 'middle', 'front'];
const ROW_SIZE: Record<RowName, number> = { back: 5, middle: 5, front: 3 };
const ROW_LABEL: Record<RowName, CopyKey> = {
  back: 'pusoy.row.back',
  middle: 'pusoy.row.middle',
  front: 'pusoy.row.front',
};
const EMPTY_ROWS: Rows = { back: [], middle: [], front: [] };

/** Milliseconds between two rows of the reveal cascade (§G4 signature moment). */
const CASCADE_MS = 620;

export function PusoyBoard(): JSX.Element {
  const match = useApp((s) => s.match);
  const t = useT();
  const lang = useLang();
  const [rows, setRows] = useState<Rows>(EMPTY_ROWS);
  const [pick, setPick] = useState<string | null>(null);
  const [stage, setStage] = useState(0);

  const view = useMemo(() => readPusoyView(match?.view), [match?.view]);
  const hand = view?.hand ?? [];
  /** Identity of the thirteen. Stable for the whole hand, so it gates the reset. */
  const handKey = hand.join(',');
  const phase = view?.phase ?? 'arrange';

  // A new deal empties the table. The thirteen never change inside one hand, so
  // this does not fight the player mid-arrangement.
  useEffect(() => {
    setRows(EMPTY_ROWS);
    setPick(null);
  }, [handKey, match?.matchId]);

  /**
   * The cascade. Rows come over one at a time, back first — the back is where
   * the big hands are, and the front, which is where most scoops are actually
   * decided, lands last. Reduced motion skips straight to the finished table
   * (§14): the animation is the drama, never the information.
   */
  useEffect(() => {
    if (phase !== 'reveal') {
      setStage(0);
      return;
    }
    if (prefersReducedMotion()) {
      setStage(4);
      return;
    }
    setStage(0);
    const ids = [1, 2, 3, 4].map((s) => window.setTimeout(() => setStage(s), s * CASCADE_MS));
    return () => ids.forEach((id) => window.clearTimeout(id));
  }, [phase, match?.matchId]);

  const rules = useMemo(() => rulesetOf(view?.ruleset), [view?.ruleset]);
  /**
   * Read off the thirteen CARDS, never off the submitted rows — a dragon is a
   * dragon however its owner laid it out, and it also cancels a misset. Getting
   * that backwards would warn a natural holder about a foul they cannot commit.
   */
  const natural = useMemo(
    () => (hand.length === 13 ? detectNatural(hand, rules) : null),
    [handKey, rules],
  );

  if (!match || !view) {
    return <GameRoom felt={<p className="room__wait">{t('pusoy.wait')}</p>} />;
  }

  const mySeat = match.seat > SPECTATOR_SEAT ? match.seat : null;
  const watching = mySeat === null;
  const locked = mySeat !== null && view.locked[mySeat] === true;
  const myTurn = mySeat !== null && view.turn === mySeat && view.over === null;

  const placed = new Set([...rows.back, ...rows.middle, ...rows.front]);
  const pool = hand.filter((c) => !placed.has(c));
  const complete =
    rows.back.length === 5 && rows.middle.length === 5 && rows.front.length === 3;

  // The misset boundary, exactly as the engine states it: back >= middle, then
  // middle >= front on the fields the front actually defines.
  const backUnderMiddle = complete && rank5(rows.back) < rank5(rows.middle);
  const middleUnderFront = complete && !middleGeFront(rows.middle, rows.front);
  const misset = complete && natural === null && !notFoul(rows.back, rows.middle, rows.front);

  const send = (action: PusoyAction): void => {
    socket.send({ t: 'match.action', matchId: match.matchId, seq: match.seq, action });
    setPick(null);
  };

  const tapCard = (card: string): void => {
    if (locked || watching) return;
    if (pick === null) {
      setPick(card);
      return;
    }
    if (pick === card) {
      setPick(null);
      return;
    }
    setRows((cur) => swapCards(cur, pick, card));
    setPick(null);
  };

  const dropInto = (slot: Slot): void => {
    if (locked || watching || pick === null) return;
    if (slot !== 'pool' && rows[slot].length >= ROW_SIZE[slot] && !rows[slot].includes(pick)) return;
    setRows((cur) => moveCard(cur, pick, slot));
    setPick(null);
  };

  /** Native drag mirrors the tap gestures exactly — same two operations. */
  const onDropRow = (e: DragEvent<HTMLElement>, slot: Slot): void => {
    e.preventDefault();
    const card = e.dataTransfer.getData('text/plain');
    if (!card || locked || watching) return;
    if (slot !== 'pool' && rows[slot].length >= ROW_SIZE[slot] && !rows[slot].includes(card)) return;
    setRows((cur) => moveCard(cur, card, slot));
    setPick(null);
  };

  const onDropCard = (e: DragEvent<HTMLElement>, target: string): void => {
    e.preventDefault();
    e.stopPropagation();
    const card = e.dataTransfer.getData('text/plain');
    if (!card || card === target || locked || watching) return;
    setRows((cur) => swapCards(cur, card, target));
    setPick(null);
  };

  const revealing = phase === 'reveal' && view.boards.length > 0;

  /**
   * The felt. Three bands at fixed table pixels, back edge to near edge, so the
   * ladder reads the same way the rules state it — and so the camera, not this
   * file, decides whether you are looking down at it or across it.
   */
  const felt = revealing ? (
    <Reveal view={view} stage={stage} mySeat={mySeat} watching={watching} />
  ) : (
    <div className="pusoy" data-locked={locked}>
      {ROW_KEYS.map((key) => {
        const cards = rows[key];
        const full = cards.length === ROW_SIZE[key];
        const bad = (key === 'back' && backUnderMiddle) || (key === 'middle' && middleUnderFront);
        const royalty = full ? royaltyOf(key, cards, rules) : 0;
        return (
          <section
            className="pusoy__row"
            key={key}
            data-row={key}
            data-bad={bad && natural === null}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => onDropRow(e, key)}
          >
            <div className="pusoy__slots">
              {Array.from({ length: ROW_SIZE[key] }, (_, i) => {
                const card = cards[i];
                if (card === undefined) {
                  return (
                    <button
                      className="pusoy__slot"
                      key={`${key}-${i}`}
                      type="button"
                      data-empty="true"
                      disabled={locked || watching || pick === null}
                      onClick={() => dropInto(key)}
                      aria-label={t('pusoy.row.empty', { row: t(ROW_LABEL[key]) })}
                    />
                  );
                }
                return (
                  <button
                    className="pusoy__slot"
                    key={card}
                    type="button"
                    data-picked={pick === card}
                    draggable={!locked && !watching}
                    onDragStart={(e) => e.dataTransfer.setData('text/plain', card)}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => onDropCard(e, card)}
                    disabled={locked || watching}
                    onClick={() => tapCard(card)}
                    aria-pressed={pick === card}
                    aria-label={cardName(card, t)}
                  >
                    <Baraha code={card} />
                  </button>
                );
              })}
            </div>
          </section>
        );
      })}
    </div>
  );

  /**
   * The near rail: everything still in your hand. Held cards are the one thing
   * the room keeps in screen space, so these stay flat under both cameras.
   */
  const railHand = (
    <div
      className="pusoy__pool"
      onDragOver={(e) => e.preventDefault()}
      onDrop={(e) => onDropRow(e, 'pool')}
    >
      {watching ? (
        <p className="pusoy__bare">{t('pusoy.rail.watching')}</p>
      ) : pool.length === 0 ? (
        <p className="pusoy__bare">{t(locked ? 'pusoy.rail.locked' : 'pusoy.rail.done')}</p>
      ) : (
        pool.map((card) => (
          <button
            className="pusoy__slot"
            key={card}
            type="button"
            data-picked={pick === card}
            draggable={!locked}
            onDragStart={(e) => e.dataTransfer.setData('text/plain', card)}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => onDropCard(e, card)}
            disabled={locked}
            onClick={() => tapCard(card)}
            aria-pressed={pick === card}
            aria-label={cardName(card, t)}
          >
            <Baraha code={card} />
          </button>
        ))
      )}
    </div>
  );

  return (
    <GameRoom
      felt={felt}
      hand={revealing ? undefined : railHand}
      toast={
        revealing ? (
          verdictLine(view, mySeat, watching, t)
        ) : natural !== null ? (
          <>
            <b>{naturalLabel(natural.kind, lang, t)}!</b> {t('pusoy.natural.auto')}
          </>
        ) : misset ? (
          // The one line on this screen that must not be missed. The button
          // under it changes its own label too, so the warning is never carried
          // by colour alone (§7.3).
          <span className="pusoy__warn" role="alert">
            <b>{t('pusoy.misset')}</b>{' '}
            {t(backUnderMiddle ? 'pusoy.misset.back' : 'pusoy.misset.middle')}{' '}
            {t('pusoy.misset.cost')}
          </span>
        ) : (
          <>
            <b>{turnLine(view, mySeat, watching, locked, t)}</b>
            {watching || locked
              ? null
              : ` ${
                  complete && !myTurn
                    ? t('pusoy.ready')
                    : hintLine(complete, pick, pool.length, t)
                }`}
          </>
        )
      }
      actions={
        revealing || watching || locked ? null : (
          <>
            <button
              className="room-act"
              data-tone={misset ? 'danger' : 'primary'}
              type="button"
              disabled={!complete || !myTurn}
              onClick={() => send({ kind: 'arrange', ...rows })}
            >
              {t(misset ? 'pusoy.act.lockAnyway' : 'pusoy.act.lock')}
            </button>
            <button
              className="room-act"
              type="button"
              disabled={hand.length !== 13}
              onClick={() => {
                const best = bestArrangement(hand, rules);
                setRows({ back: best.back, middle: best.middle, front: best.front });
                setPick(null);
              }}
            >
              {t('pusoy.act.auto')}
            </button>
          </>
        )
      }
      aside={
        <>
          {/*
            The row markers live in the HUD, not on the felt.
            
            They used to stand on the table beside their row, which worked while
            there was one camera. With the chair adjustable they cannot: the
            three rows are 84px apart on the surface, so at a steep tilt they
            project 17px apart and three labels do not fit in 17px — they slid
            into each other and, at a wide pan, off the rim into the dark. A
            label that must stay readable belongs in screen space; the cards
            themselves are the game and stay on the felt where they belong.
          */}
          {!revealing && !locked ? (
            <div className="pusoy__rows" role="group" aria-label={t('pusoy.rows.label')}>
              {ROW_KEYS.map((key) => {
                const cards = rows[key];
                const full = cards.length === ROW_SIZE[key];
                const bad =
                  (key === 'back' && backUnderMiddle) || (key === 'middle' && middleUnderFront);
                const royalty = full ? royaltyOf(key, cards, rules) : 0;
                return (
                  <button
                    key={key}
                    className="pusoy__rowtag"
                    type="button"
                    data-bad={bad && natural === null}
                    onClick={() => dropInto(key)}
                    disabled={
                      locked || watching || pick === null || (full && !cards.includes(pick ?? ''))
                    }
                  >
                    <span className="pusoy__tagtop">
                      <b>{t(ROW_LABEL[key])}</b>
                      <span>
                        {cards.length}/{ROW_SIZE[key]}
                      </span>
                    </span>
                    <em>
                      {full ? t(CATEGORY_LABEL[classOf(key, cards)]) : t('pusoy.row.short')}
                      {royalty > 0 ? <i>+{royalty}</i> : null}
                    </em>
                  </button>
                );
              })}
            </div>
          ) : null}
        {revealing ? (
          <div className="pusoy__scoops" data-shown={stage >= 4}>
            {scoopLines(view, mySeat, watching, t).map((line) => (
              <p key={line}>
                <Icon name="baraha" size={12} />
                {line}
              </p>
            ))}
          </div>
        ) : (
          /* Who has already locked in. The seat pods carry the clock; this is
             the fact they cannot, and it is what the table is waiting on. */
          <div className="pusoy__seats">
            {view.counts.map((n, seat) => (
              <div
                className="pusoy__seat"
                key={seat}
                data-turn={view.turn === seat}
                data-locked={view.locked[seat] === true}
              >
                <span>{seatName(seat, mySeat, watching, t)}</span>
                {view.quit.includes(seat) ? (
                  <em>{t('pusoy.seat.quit')}</em>
                ) : view.locked[seat] ? (
                  <em>{t(view.auto[seat] ? 'pusoy.seat.clock' : 'pusoy.seat.locked')}</em>
                ) : null}
                <b className="data">{n}</b>
              </div>
            ))}
          </div>
        )}
        </>
      }
    />
  );
}

/* ── The reveal cascade ───────────────────────────────────────────────────── */

/** Which stage each row comes over on. Back first; the front lands last. */
const ROW_STAGE: Record<RowName, number> = { back: 1, middle: 2, front: 3 };

/**
 * Every board, face up on the felt. Nothing here stands up: a showdown is
 * thirteen cards pushed flat across a table, and at four seats a forest of
 * standing labels would hide the very cards it was naming. Two columns at most,
 * so a third and fourth seat fold into a square — see the note in the CSS for
 * why the felt cannot spend the height a wider line would cost.
 */
function Reveal({
  view,
  stage,
  mySeat,
  watching,
}: {
  view: PusoyView;
  stage: number;
  mySeat: number | null;
  watching: boolean;
}): JSX.Element {
  const t = useT();
  const lang = useLang();
  const units = view.over?.units ?? [];
  const boards = [...view.boards].sort((a, b) => a.seat - b.seat);

  return (
    <div className="pusoy pusoy--reveal">
      <div
        className="pusoy__boards"
        style={{ '--cols': Math.min(boards.length, 2) } as CSSProperties}
      >
        {boards.map((board) => {
          const net = units[board.seat] ?? 0;
          return (
            <section
              className="pusoy__col"
              key={board.seat}
              data-mine={board.seat === mySeat}
              data-misset={board.misset}
            >
              <p className="pusoy__colwho">
                <b>{seatName(board.seat, mySeat, watching, t)}</b>
                {board.auto ? <em>{t('pusoy.seat.clock')}</em> : null}
                {board.natural ? <i data-nat="true">{naturalLabel(board.natural, lang, t)}</i> : null}
                {board.misset ? <i data-bad="true">{t('pusoy.reveal.misset')}</i> : null}
              </p>

              {ROW_KEYS.map((key) => {
                const shown = stage >= ROW_STAGE[key];
                const cards = board[key];
                return (
                  <div className="pusoy__revrow" key={key} data-shown={shown} data-row={key}>
                    <div className="pusoy__revcards">
                      {cards.map((card, i) =>
                        shown ? (
                          <Baraha key={card} code={card} small />
                        ) : (
                          <span className="pusoy__card pusoy__card--back" key={`${key}-${i}`} aria-hidden="true" />
                        ),
                      )}
                    </div>
                    <span className="pusoy__revclass">
                      {shown ? t(CATEGORY_LABEL[board.classes[key]]) : '···'}
                    </span>
                  </div>
                );
              })}

              <p className="pusoy__coltotal" data-shown={stage >= 4}>
                {board.royalties > 0 ? (
                  <em>{t('pusoy.reveal.royalty', { n: board.royalties })}</em>
                ) : null}
                <b className="data" data-sign={net > 0 ? 'up' : net < 0 ? 'down' : 'flat'}>
                  {net > 0 ? `+${net}` : net}
                </b>
              </p>
            </section>
          );
        })}
      </div>
    </div>
  );
}

/* ── The card ─────────────────────────────────────────────────────────────── */

/**
 * Plastic-coated face, four-colour suits (component sheet 1d), at the rooftop
 * mock's own metrics — 46×64 with the index boxed top-left and the big glyph
 * bottom-right. The glyph is the read; the hue is a redundant second cue, so
 * nothing depends on telling red from black (§7.3).
 *
 * The class is venue-prefixed because these cards live in two places the room
 * puts far apart — the felt and the near rail — and an unscoped `.baraha` would
 * collide with Tongits' own copy of this markup.
 *
 * Third copy of this markup, after Tongits and Pusoy Dos — it wants to be a
 * primitive in components/, which is a move none of the three boards owns.
 */
function Baraha({ code, small = false }: { code: string; small?: boolean }): JSX.Element {
  const suit = code.slice(-1);
  return (
    <span className="pusoy__card" data-suit={suit} data-small={small} aria-hidden="true">
      <span className="pusoy__idx">
        <b>{code.slice(0, -1)}</b>
        <i>{SUIT_GLYPH[suit] ?? '?'}</i>
      </span>
      {small ? null : <span className="pusoy__mid">{SUIT_GLYPH[suit] ?? '?'}</span>}
    </span>
  );
}

const SUIT_GLYPH: Record<string, string> = { S: '♠', H: '♥', D: '♦', C: '♣' };
const SUIT_NAME: Record<string, CopyKey> = {
  S: 'pusoy.suit.S',
  H: 'pusoy.suit.H',
  D: 'pusoy.suit.D',
  C: 'pusoy.suit.C',
};

function cardName(code: string, t: T): string {
  const suit = SUIT_NAME[code.slice(-1)];
  return t('pusoy.card', { rank: code.slice(0, -1), suit: suit ? t(suit) : '?' });
}

/* ── Arranging ────────────────────────────────────────────────────────────── */

function cloneRows(rows: Rows): Rows {
  return { back: [...rows.back], middle: [...rows.middle], front: [...rows.front] };
}

function slotOf(rows: Rows, card: string): Slot {
  for (const key of ROW_KEYS) if (rows[key].includes(card)) return key;
  return 'pool';
}

/** Lift a card out of wherever it is and drop it into `to`. */
function moveCard(rows: Rows, card: string, to: Slot): Rows {
  const next = cloneRows(rows);
  for (const key of ROW_KEYS) {
    const i = next[key].indexOf(card);
    if (i >= 0) next[key].splice(i, 1);
  }
  if (to !== 'pool') next[to].push(card);
  return next;
}

/**
 * Trade two cards. This is the gesture the game is actually played with — a
 * finished board is rarely built card by card, it is built by arguing over which
 * two should change places — so it works between any two cards anywhere,
 * including one still in the pool.
 */
function swapCards(rows: Rows, a: string, b: string): Rows {
  const next = cloneRows(rows);
  const la = slotOf(next, a);
  const lb = slotOf(next, b);
  if (la === lb) {
    if (la === 'pool') return next; // the pool is display order; nothing to trade
    const arr = next[la];
    const i = arr.indexOf(a);
    const j = arr.indexOf(b);
    if (i < 0 || j < 0) return next;
    arr[i] = b;
    arr[j] = a;
    return next;
  }
  if (la !== 'pool') {
    const arr = next[la];
    const i = arr.indexOf(a);
    if (i >= 0) arr[i] = b;
  }
  if (lb !== 'pool') {
    const arr = next[lb];
    const j = arr.indexOf(b);
    if (j >= 0) arr[j] = a;
  }
  return next;
}

/** The engine's own key for a finished row. Front is three cards, never five. */
function classOf(row: RowName, cards: readonly string[]): Category {
  return categoryOf(row === 'front' ? rank3(cards) : rank5(cards));
}

function royaltyOf(row: RowName, cards: readonly string[], rules: PusoyRuleset): number {
  return royaltyUnits(row, row === 'front' ? rank3(cards) : rank5(cards), rules);
}

/**
 * The variant this table is scored under. An unknown key falls back to the
 * standard table: the hints on this screen are hints, and a board that refused
 * to render because it did not recognise a ruleset name would be worse than one
 * that quotes slightly the wrong royalty.
 */
function rulesetOf(key: string | undefined): PusoyRuleset {
  if (typeof key !== 'string') return STANDARD;
  return RULESETS[key] ?? STANDARD;
}

function prefersReducedMotion(): boolean {
  return typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/* ── Reading the engine's view ────────────────────────────────────────────── */

/**
 * The view arrives as `unknown` (the wire type must not know the rules), so it
 * is narrowed rather than cast. A malformed payload renders the waiting state
 * instead of throwing inside a live match.
 */
export function readPusoyView(raw: unknown): PusoyView | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const v = raw as Record<string, unknown>;
  if (!Array.isArray(v['counts'])) return null;

  const phase = v['phase'] === 'reveal' ? 'reveal' : 'arrange';
  return {
    seats: isNum(v['seats']) ? v['seats'] : v['counts'].length,
    you: isNum(v['you']) ? v['you'] : null,
    // Display order is the engine's own: strongest first, suits in deck order.
    hand: cardList(v['hand']).sort(byStrength),
    counts: v['counts'].filter(isNum),
    locked: boolList(v['locked']),
    auto: boolList(v['auto']),
    turn: isNum(v['turn']) ? v['turn'] : null,
    phase,
    boards: Array.isArray(v['boards'])
      ? v['boards'].map(readBoard).filter((b): b is RevealedBoard => b !== null)
      : [],
    quit: numList(v['quit']),
    over: readOver(v['over']),
    ruleset: typeof v['ruleset'] === 'string' ? v['ruleset'] : 'standard',
  };
}

function readBoard(raw: unknown): RevealedBoard | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const b = raw as Record<string, unknown>;
  if (!isNum(b['seat'])) return null;
  const back = cardList(b['back']);
  const middle = cardList(b['middle']);
  const front = cardList(b['front']);
  if (back.length !== 5 || middle.length !== 5 || front.length !== 3) return null;
  const classes = typeof b['classes'] === 'object' && b['classes'] !== null
    ? (b['classes'] as Record<string, unknown>)
    : {};
  return {
    seat: b['seat'],
    back,
    middle,
    front,
    classes: {
      // The engine publishes the class per row; deriving it again from the cards
      // is the fallback for a payload from an older or newer server.
      back: readCategory(classes['back']) ?? categoryOf(rank5(back)),
      middle: readCategory(classes['middle']) ?? categoryOf(rank5(middle)),
      front: readCategory(classes['front']) ?? categoryOf(rank3(front)),
    },
    misset: b['misset'] === true,
    natural: typeof b['natural'] === 'string' ? b['natural'] : null,
    royalties: isNum(b['royalties']) ? b['royalties'] : 0,
    auto: b['auto'] === true,
  };
}

function readCategory(raw: unknown): Category | null {
  return typeof raw === 'string' && raw in CATEGORY_LABEL ? (raw as Category) : null;
}

function readOver(raw: unknown): PusoyOverView | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const o = raw as Record<string, unknown>;
  return {
    reason: typeof o['reason'] === 'string' ? o['reason'] : 'showdown',
    winner: isNum(o['winner']) ? o['winner'] : null,
    units: numList(o['units']),
    order: numList(o['order']),
    pairwise: Array.isArray(o['pairwise'])
      ? o['pairwise'].map(readPair).filter((p): p is PairResultView => p !== null)
      : [],
  };
}

function readPair(raw: unknown): PairResultView | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const p = raw as Record<string, unknown>;
  if (!isNum(p['a']) || !isNum(p['b'])) return null;
  const scoop = typeof p['scoop'] === 'object' && p['scoop'] !== null
    ? (p['scoop'] as Record<string, unknown>)
    : null;
  return {
    a: p['a'],
    b: p['b'],
    unitsA: isNum(p['unitsA']) ? p['unitsA'] : 0,
    unitsB: isNum(p['unitsB']) ? p['unitsB'] : 0,
    scoop:
      scoop && (scoop['winner'] === 'a' || scoop['winner'] === 'b')
        ? { winner: scoop['winner'], bonus: isNum(scoop['bonus']) ? scoop['bonus'] : 0 }
        : null,
  };
}

const isNum = (x: unknown): x is number => typeof x === 'number' && Number.isFinite(x);
const isCard = (x: unknown): x is string => typeof x === 'string' && x.length >= 2;
const cardList = (x: unknown): string[] => (Array.isArray(x) ? x.filter(isCard) : []);
const numList = (x: unknown): number[] => (Array.isArray(x) ? x.filter(isNum) : []);
const boolList = (x: unknown): boolean[] => (Array.isArray(x) ? x.map((b) => b === true) : []);

/* ── Copy ─────────────────────────────────────────────────────────────────── */

const CATEGORY_LABEL: Record<Category, CopyKey> = {
  highCard: 'pusoy.cat.highCard',
  pair: 'pusoy.cat.pair',
  twoPair: 'pusoy.cat.twoPair',
  trips: 'pusoy.cat.trips',
  straight: 'pusoy.cat.straight',
  flush: 'pusoy.cat.flush',
  fullHouse: 'pusoy.cat.fullHouse',
  quads: 'pusoy.cat.quads',
  straightFlush: 'pusoy.cat.straightFlush',
};

/**
 * The engine's own display name for a natural. Keyed by a string off the wire,
 * so it is looked up rather than indexed — a kind this build has not heard of
 * still renders as "natural" instead of blanking the badge.
 */
function naturalLabel(kind: string, lang: Lang, t: T): string {
  const meta = Object.entries(NATURAL_META).find(([key]) => key === kind)?.[1];
  return meta?.[lang] ?? t('pusoy.natural.unknown');
}

function seatName(seat: number, mySeat: number | null, watching: boolean, t: T): string {
  if (!watching && seat === mySeat) return t('pusoy.seat.you');
  return t('pusoy.seat.n', { n: seat + 1 });
}

function turnLine(
  v: PusoyView,
  mySeat: number | null,
  watching: boolean,
  locked: boolean,
  t: T,
): string {
  if (v.phase === 'reveal') return t('pusoy.turn.reveal');
  if (v.turn === null) return t('pusoy.turn.wait');
  if (watching) return t('pusoy.turn.arranging', { who: seatName(v.turn, mySeat, watching, t) });
  if (v.turn === mySeat) return t(locked ? 'pusoy.turn.locked' : 'pusoy.turn.you');
  return t(locked ? 'pusoy.turn.lockedwait' : 'pusoy.turn.theirs', {
    who: seatName(v.turn, mySeat, watching, t),
  });
}

function hintLine(complete: boolean, pick: string | null, poolLeft: number, t: T): string {
  if (pick !== null) return t('pusoy.hint.holding');
  if (!complete) return t('pusoy.hint.left', { n: poolLeft });
  return t('pusoy.hint.ok');
}

function verdictLine(v: PusoyView, mySeat: number | null, watching: boolean, t: T): string {
  const over = v.over;
  if (!over) return t('pusoy.over.counting');
  if (over.reason === 'tabla') return t('pusoy.over.tabla');
  if (over.reason === 'forfeit') return t('pusoy.over.forfeit');
  if (over.winner === null) return t('pusoy.over.none');
  if (!watching && over.winner === mySeat) return t('pusoy.over.you');
  return t('pusoy.over.who', { who: seatName(over.winner, mySeat, watching, t) });
}

/**
 * Every scoop at the table, as a line. A scoop is all three rows taken outright
 * against one opponent, and §G4 asks for it to be celebrated — so it is called
 * out by name rather than left inside a units column.
 */
function scoopLines(v: PusoyView, mySeat: number | null, watching: boolean, t: T): string[] {
  const out: string[] = [];
  for (const pair of v.over?.pairwise ?? []) {
    if (!pair.scoop) continue;
    const winner = pair.scoop.winner === 'a' ? pair.a : pair.b;
    const loser = pair.scoop.winner === 'a' ? pair.b : pair.a;
    if (!watching && winner === mySeat)
      out.push(t('pusoy.scoop.you', { who: seatName(loser, mySeat, watching, t) }));
    else if (!watching && loser === mySeat)
      out.push(t('pusoy.scoop.onyou', { who: seatName(winner, mySeat, watching, t) }));
    else
      out.push(
        t('pusoy.scoop.other', {
          winner: seatName(winner, mySeat, watching, t),
          loser: seatName(loser, mySeat, watching, t),
        }),
      );
  }
  return out;
}
